import { useEffect, useState } from "react";
import { Outlet, createRootRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useIdentities, useMe } from "../api/queries.ts";
import { api } from "../api/client.ts";
import { enableHant } from "../lib/hant.ts";
import { applySiteConfig, useSiteConfig, type SiteConfig } from "../lib/site-config.ts";
import {
  derivePriceView,
  onIdentityChanged,
  readIdentity,
  readPurchaseAs,
  readViewAs,
  writePurchaseAs,
  writeViewAs,
} from "../lib/identity.ts";
import { SessionExpiredBar } from "../components/session-expired-bar.tsx";

/**
 * ═══════════════════════════════════════════════════════════════
 *  价格视角同步
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **必须在根组件上算，不能只在「我的」那一页算。**
 *
 *    以前只有 `/me` 在推导采购身份 —— 于是登录之后直接落在**首页**的人
 *    （绝大多数人都是这样）根本没经过那段代码，采购身份是空的，
 *    有上级的商家一进来看到的是**平台指导价**：那是给"自己注册、没有上级"
 *    的陌生人看的最贵一档，他这辈子都不会按那个价买。
 *
 * ⚠️ 视角变了要**把已经取回来的数据全作废** —— 那些是按旧价拿的，
 *    不作废的话页面上会停在旧价格，直到用户自己刷新。
 */
function usePriceViewSync() {
  const { data: me } = useMe();
  const { data: identities } = useIdentities(Boolean(me));
  const qc = useQueryClient();

  /** ⚠️ 「我的」页切身份是直接写 localStorage 的，靠这个事件把变化传过来。 */
  const [tick, setTick] = useState(0);
  useEffect(() => onIdentityChanged(() => setTick((n) => n + 1)), []);

  useEffect(() => {
    /**
     * 🔴 **没登录就必须清干净。**
     *    不清的话，上一个人退出后，下一个人（甚至游客）会带着他的采购身份逛，
     *    看到的是别人的进货价。
     */
    if (!me) {
      if (readPurchaseAs() !== null) {
        writePurchaseAs(null);
        void qc.invalidateQueries();
      }
      return;
    }
    if (!identities) return;

    const next = derivePriceView(identities.orgs, readIdentity());
    if (readPurchaseAs() === next.purchaseAs && readViewAs() === next.viewAs) return;

    writePurchaseAs(next.purchaseAs);
    writeViewAs(next.viewAs);
    /** ⚠️ 价格全变了 —— 商品、购物车、结算试算一律重取。 */
    void qc.invalidateQueries();
  }, [me, identities, qc, tick]);
}

/**
 * ═══════════════════════════════════════════════════════════════
 *  全站外观同步
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **配色 / 简繁 / 货币是平台后台定的全站设置，跟登录状态无关。**
 *    所以放在根组件、不带任何鉴权 —— 登录页、游客逛店都要按它渲染。
 *
 * ⚠️ **`staleTime` 给足**：这条接口每次进站都会打，而这三个值一天也改不了
 *    一次。取太勤只是白白多几百个请求。后台改完，用户下次进站或过了缓存就生效。
 */
function useSiteConfigSync() {
  const { data } = useQuery({
    queryKey: ["site-config"],
    queryFn: () => api.get<SiteConfig>("/site-config"),
    staleTime: 5 * 60_000,
    retry: false,
  });

  useEffect(() => {
    if (data) applySiteConfig(data);
  }, [data]);

  /**
   * 🔴 **繁体转换要在整棵树都挂上去之后再开**，而且切回简体要能关掉。
   *    ⚠️ 关掉只是停止继续转，**不会把已经转过的字转回去** ——
   *       一简对多繁不可逆（「後」和「后」都要变回「后」）。
   *       切回简体后刷新一次就是干净的简体。
   */
  const { script } = useSiteConfig();
  useEffect(() => {
    if (script !== "hant") return;
    return enableHant();
  }, [script]);
}

function Root() {
  useSiteConfigSync();
  usePriceViewSync();
  return (
    <>
      {/*
        🔴 **「登录已过期」的提示挂在最外层** —— 过期是全站的事，
           不能只在某一页出现。它自己判断要不要显示（见 `lib/session.ts`）。
      */}
      <SessionExpiredBar />
      <Outlet />
    </>
  );
}

export const rootRoute = createRootRoute({ component: Root });
